// ================================================
// AUTH HOOK
// ================================================
// Hook for accessing authenticated user data

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';

interface AuthUser extends User {
  company_id?: string;
}

interface UseAuthReturn {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    const getUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          setUser(null);
          setIsLoading(false);
          return;
        }

        if (session?.user) {
          // Fetch user's company_id from users table
          const { data: userData } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', session.user.id)
            .single();

          setUser({
            ...session.user,
            company_id: userData?.company_id,
          });
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error in useAuth:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    getUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (session?.user) {
          // Fetch company_id when auth state changes
          const { data: userData } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', session.user.id)
            .single();

          setUser({
            ...session.user,
            company_id: userData?.company_id,
          });
        } else {
          setUser(null);
        }
        setIsLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
  };
}
